import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiInformationCircle } from "react-icons/hi";
import TextField from "./components/TextField";
import PasswordField from "./components/PasswordField";
import MessageBox from "./components/MessageBox";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/context/AdminContext";

const Login = () => {
  const { adminLogin, sysAdmin } = useAdminContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleResponse = (isError, msg) => {
    setError(isError);
    setSuccess(!isError);
    setLoading(false);
    setErrorMessage(msg);
  };

  const sendLoginRequest = async (e) => {
    e.preventDefault(); // Prevents HTML form submission refresh
    setLoading(true);

    if (!email.trim()) {
      return handleResponse(true, "Please enter your email address.");
    }
    if (!password) {
      return handleResponse(true, "Please enter your password.");
    }

    const { message, status } = await adminLogin({
      login: email.trim(),
      password: password,
    });

    if (status !== 200 && status !== 201) {
      handleResponse(true, message);
    }
  };

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className="flex min-h-screen w-full bg-white font-sans text-gray-900">

      {/* Left Column: Brand & Visuals (Desktop Only) */}
      <aside className="relative hidden w-1/2 overflow-hidden bg-zinc-900 lg:block">
        {/* Replace this Unsplash URL with your own high-quality African tourism asset */}
        <img
          src="https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?q=80&w=2572&auto=format&fit=crop"
          alt="African Safari Landscape"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div>
            <img src={dosee_logo} alt="Dósee Africa" className="h-10 w-auto brightness-0 invert" />
          </div>

          <div className="max-w-md text-white">
            <h2 className="text-4xl font-bold tracking-tight">Curate the extraordinary.</h2>
            <p className="mt-4 text-lg font-light text-gray-300">
              Welcome back to the Admin Portal. Manage experiences, empower hosts, and connect the world to the wonders of Africa.
            </p>
          </div>
        </div>
      </aside>

      {/* Right Column: Authentication Form */}
      <main className="flex w-full items-center justify-center p-8 lg:w-1/2 lg:p-12 xl:p-24">
        <div className="w-full max-w-sm">

          {/* Mobile Logo */}
          <div className="mb-10 flex justify-center lg:hidden">
            <img src={dosee_logo} alt="Dósee Africa" className="h-12 w-auto" />
          </div>

          <header className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Sign in</h1>
            <p className="mt-2 text-sm text-gray-600">
              Secure access for Dósee Africa administrators.
            </p>
          </header>

          <form onSubmit={sendLoginRequest} className="space-y-6">
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <TextField
                  value={email}
                  type="email"
                  placeholder="admin@doseeafrica.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Password
                </label>
                <PasswordField
                  password={password}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  placeholder="••••••••"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(false);
                  }}
                />
              </div>

              {/* Error/Success Messages */}
              <MessageBox
                error={error}
                success={success}
                message={message}
                onClick={() => {
                  setError(false);
                  setSuccess(false);
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-lg bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? (
                <AiOutlineLoading className="animate-spin text-xl" />
              ) : (
                "Sign In to Dashboard"
              )}
            </button>

            <div className="mt-6 flex items-start gap-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
              <HiInformationCircle className="mt-0.5 shrink-0 text-emerald-600" size={16} />
              <p>
                By continuing, you agree to Dósee Africa's{" "}
                <Link to="/terms" className="font-medium underline hover:text-gray-900">Terms</Link> and{" "}
                <Link to="/privacy" className="font-medium underline hover:text-gray-900">Privacy Policy</Link>.
              </p>
            </div>
          </form>

          <div className="mt-8 flex flex-col items-center gap-4 border-t border-gray-100 pt-8 text-sm">
            <Link to="/signup" className="font-medium text-emerald-700 hover:text-emerald-800">
              Create a company account &rarr;
            </Link>
            <p className="text-gray-500">
              Need help? <a href="mailto:support@doseeafrica.com" className="underline hover:text-gray-900">Contact IT Support</a>
            </p>
          </div>

        </div>
      </main>

    </div>
  );
};

export default Login;